/* eslint-disable react/prop-types */
import {
  FaBan,
  FaExclamationTriangle,
  FaLock,
  FaSearch,
  FaServer,
  FaWifi,
} from "react-icons/fa";
import SEOData from "../SEO/SEO";
import { Link } from "react-router-dom";
import { buildLoginUrl, currentPath } from "../../utils/authRedirect";
import { DASHBOARD_HOME, lastAllowedPath, markDenied } from "../../utils/lastDashboardPath";

// Pull every human-readable message out of the backend error envelope
// ({ status, status_code, errors: { field: [msg] } }) — see
// core/exceptions.py. Falls back to plain DRF `detail` bodies.
const extractMessages = (data) => {
  if (!data) return [];
  if (typeof data === "string") return [data];
  const source = data.errors || data;
  if (typeof source === "string") return [source];
  if (Array.isArray(source)) return source.map(String);
  return Object.values(source).flatMap((value) =>
    Array.isArray(value) ? value.map(String) : [String(value)]
  );
};

// Map an RTK Query error to what actually went wrong. Status is a number for
// HTTP errors, or a string ("FETCH_ERROR", "TIMEOUT_ERROR", "PARSING_ERROR")
// when the request never got a usable response.
const describeError = (errorData) => {
  const status = errorData?.status;
  const messages = extractMessages(errorData?.data);
  const invalidPage = messages.some((m) => /invalid page/i.test(m));

  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return {
      icon: FaWifi,
      title: "Connection Problem",
      subtitle: "We couldn't reach the server",
      description:
        "Check your internet connection and try again. If the problem continues, the server may be temporarily unavailable.",
      label: status === "TIMEOUT_ERROR" ? "Request timed out" : "Network error",
      canRetry: true,
    };
  }
  if (status === "PARSING_ERROR") {
    return {
      icon: FaServer,
      title: "Unexpected Response",
      subtitle: "The server sent a response we couldn't read",
      description:
        "This is usually temporary. Please reload the page. If it keeps happening, contact support.",
      label: "Invalid response",
      canRetry: true,
    };
  }
  if (status === 401) {
    return {
      icon: FaLock,
      title: "Session Expired",
      subtitle: "Please sign in again",
      description: "Your login session has ended or is no longer valid.",
      label: "Not authenticated",
      canSignIn: true,
    };
  }
  if (status === 403) {
    return {
      icon: FaBan,
      title: "Access Denied",
      subtitle: "You do not have access",
      description:
        "You do not have the required permissions to access this resource. Please contact your administrator to request access.",
      label: "Permission error",
    };
  }
  if (status === 404 && invalidPage) {
    return {
      icon: FaSearch,
      title: "Page Out of Range",
      subtitle: "That page of results no longer exists",
      description:
        "The list is shorter than the page you were on, usually because filters changed or items were removed. Go back to the first page or clear your filters.",
      label: "Invalid page",
    };
  }
  if (status === 404) {
    return {
      icon: FaSearch,
      title: "Not Found",
      subtitle: "We couldn't find what you were looking for",
      description:
        "The item may have been deleted, or the link you followed is out of date.",
      label: "Not found",
    };
  }
  if (status === 429) {
    return {
      icon: FaExclamationTriangle,
      title: "Too Many Requests",
      subtitle: "Please slow down",
      description: "You've made too many requests in a short time. Wait a moment and try again.",
      label: "Rate limited",
      canRetry: true,
    };
  }
  if (typeof status === "number" && status >= 500) {
    return {
      icon: FaServer,
      title: "Server Error",
      subtitle: "Something went wrong on our end",
      description:
        "This isn't caused by anything you did. Please try again, or contact support if the problem persists.",
      label: "Server error",
      canRetry: true,
    };
  }
  return {
    icon: FaExclamationTriangle,
    title: "Something Went Wrong",
    subtitle: "The request could not be completed",
    description: "Please try again. If the problem continues, contact support.",
    label: "Request failed",
    canRetry: true,
  };
};

// Generic full-page error for failed queries. Name kept for backwards
// compatibility — it now reports the real cause instead of always claiming a
// permission problem.
const PermissionDenied = ({ pageTitle, errorData }) => {
  const { icon: Icon, title, subtitle, description, label, canRetry, canSignIn } =
    describeError(errorData);
  const messages = extractMessages(errorData?.data);
  const status = errorData?.status;

  // Inside the dashboard, a refused or missing page offers a way out: back to
  // the last page they could open, or the dashboard home.
  const here = `${window.location.pathname}${window.location.search}`;
  const inDashboard = window.location.pathname.startsWith(DASHBOARD_HOME);
  const offerWayOut = inDashboard && (status === 403 || status === 404);
  if (offerWayOut) markDenied(here);
  const backTo = offerWayOut ? lastAllowedPath(here) : null;

  return (
    <div className="bg-slate-100 w-full h-full">
      <SEOData
        title={`Dashboard || ${pageTitle}`}
        description={description}
        canonical={window.location.pathname}
      />
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 px-4">
        {/* Icon */}
        <div className="mb-6">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center">
            <Icon className="text-gray-500 w-14 h-14" />
          </div>
        </div>

        {/* Main message */}
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">{title}</h1>
        <p className="text-lg text-gray-700 mb-4">{subtitle}</p>
        <p className="text-gray-600 text-center mb-8 max-w-2xl">{description}</p>

        {/* Summary Section */}
        <div className="w-full max-w-2xl bg-white rounded-lg p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Summary</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 font-medium">Error</p>
              <p className="text-gray-800">{label}</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Error code</p>
              <p className="text-gray-800">{status ?? "—"}</p>
            </div>
            {messages.length > 0 && (
              <div className="col-span-2">
                <p className="text-gray-600 font-medium">Server message</p>
                {messages.map((msg, i) => (
                  <p key={i} className="text-gray-800 break-words">
                    {msg}
                  </p>
                ))}
              </div>
            )}
          </div>
          {canRetry && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:opacity-90"
            >
              Try again
            </button>
          )}
          {offerWayOut && (
            <div className="mt-6 flex flex-wrap gap-3">
              {backTo !== DASHBOARD_HOME && (
                <Link
                  to={backTo}
                  replace
                  className="px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:opacity-90"
                >
                  ← Go back
                </Link>
              )}
              <Link
                to={DASHBOARD_HOME}
                replace
                className={`px-4 py-2 rounded-md text-sm font-medium ${backTo !== DASHBOARD_HOME
                  ? "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  : "bg-brand-primary text-white hover:opacity-90"}`}
              >
                Go to Dashboard
              </Link>
            </div>
          )}
          {canSignIn && (
            <a
              href={buildLoginUrl(currentPath(), "expired")}
              className="inline-block mt-6 px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:opacity-90"
            >
              Sign in
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionDenied;
